"use client";

import React from "react";
import {
  Card as MuiCard,
  CardProps as MuiCardProps,
  CardMedia,
  Typography,
  Box,
  Stack,
  Chip
} from "@mui/material";
import { ColletteTextButton } from "@collette-travel/collette-common-controls";
import sanitizeHtml from "@/utils/sanitize-html";

export interface CardProps extends MuiCardProps {
  image?: string;
  imageAlt?: string;
  name: string;
  description: string;
  href: string;
  id: string;
  ctaText: string;
  highlight?: boolean;
}

const TravelStyleCard = ({
  image,
  imageAlt = "image alt",
  name,
  description,
  id,
  href,
  ctaText,
  highlight,
  ...rest
}: CardProps) => (
  <MuiCard
    {...rest}
    variant="outlined"
    data-testid={`travel-style-descriptions_${id}_card`}
    sx={{
      backgroundColor: "background.default",
      "&.MuiPaper-outlined": {
        backgroundColor: "background.default"
      },
      width: "20rem",
      borderRadius: "1rem",
      display: "flex",
      flexDirection: "column",
      border: "2px solid",
      borderColor: highlight ? "primary.main" : "grey.100"
    }}
  >
    {image && (
      <CardMedia
        component="img"
        image={image}
        alt={imageAlt}
        sx={{
          objectFit: "cover",
          objectPosition: "center",
          height: "15rem"
        }}
        data-testid={`travel-style-descriptions_${id}_hero`}
      />
    )}
    <Stack
      sx={{
        flexGrow: 1,
        flexDirection: "column",
        justifyContent: "space-between",
        padding: "1rem",
        gap: "1rem"
      }}
    >
      <Typography
        sx={{
          fontSize: "1.6rem",
          fontWeight: 700
        }}
        data-testid={`travel-style-descriptions_${id}_header`}
      >
        {name}
      </Typography>
      <Typography
        sx={{
          minHeight: "3lh",
          fontSize: "1.2rem"
        }}
        data-testid={`travel-style-descriptions_${id}_description`}
      >
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(description?.replace(/<p>/g, "").replace(/<\/p>/g, ""))
          }}
        />
      </Typography>
      <Box sx={{ minHeight: "2.6rem" }}>
        {highlight && (
          <Chip
            label="This Tour"
            sx={{
              alignSelf: "flex-start",
              bgcolor: "background.cool_strong",
              color: "text.primary",
              borderRadius: "8px",
              height: "2.6rem",
              padding: "0.3rem 0.8rem",
              fontSize: "1.2rem",
              fontWeight: 500
            }}
          />
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end"
        }}
      >
        <ColletteTextButton
          component="a"
          href={href}
          color="primary"
          label={ctaText}
          size="medium"
          testId={`travel-style-descriptions_${id}_text-button`}
          sx={{
            fontSize: "1.4rem !important",
            "&:focus-visible": {
              border: "1px solid",
              borderColor: "primary.main"
            }
          }}
        />
      </Box>
    </Stack>
  </MuiCard>
);

export default TravelStyleCard;
